/**
 * Diagnostics Data Store - Phase 14 (Backend-connected)
 *
 * Fetches real data from /api/diagnostics/ endpoints.
 * Falls back to client-side data if backend is unreachable.
 */
import axios from 'axios';
import { buildDiagnosticsBundle, summarizeQueueSnapshot } from './diagnosticsModel';
import { sanitizeDiagnosticsBundle } from './sanitizer';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  // Check multiple token keys used by different parts of the app
  const token = localStorage.getItem('backoffice_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Backend-connected getters ───

export async function getDiagnosticsBundle(scope = {}, user = {}) {
  try {
    const { data } = await axios.get(`${API}/api/diagnostics/bundle`, {
      headers: authHeaders(),
    });
    return {
      generatedAt: data.generated_at || new Date().toISOString(),
      appContext: {
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'production',
        browser: detectBrowser(),
        deviceType: /Mobi/.test(navigator.userAgent) ? 'mobile' : 'desktop',
      },
      queueSnapshot: {
        pendingCount: data.orders?.filter(o => o.status === 'pending').length || 0,
        failedCount: data.orders?.filter(o => o.status === 'cancelled').length || 0,
        conflictCount: 0,
        oldestPendingAgeMs: null,
        byDomain: {},
      },
      incidentSummary: {
        openCount: data.alerts?.filter(a => ['open','acknowledged','escalated'].includes(a.status)).length || 0,
        criticalCount: data.alerts?.filter(a => a.severity === 'critical').length || 0,
        blockedPaymentCount: 0,
        escalatedCount: data.alerts?.filter(a => a.status === 'escalated').length || 0,
        unresolvedIncidents: (data.alerts || []).map(a => ({
          incidentId: a.alert_id,
          title: a.title,
          domain: a.source_domain || a.alert_type,
          severity: a.severity,
          occurredAt: a.created_at,
        })),
      },
      posRecoverySummary: {
        unsyncedOrders: data.orders?.filter(o => o.payment_status === 'pending').length || 0,
        blockedPayments: data.transactions?.filter(t => ['declined', 'error', 'timed_out'].includes(t.status)).length || 0,
        failedKitchen: 0,
        failedReceipts: 0,
        staleItems: 0,
      },
      networkDiagnostics: {
        currentStatus: navigator.onLine ? 'online' : 'offline',
        backendReachable: true,
        effectiveConnection: { type: navigator.connection?.effectiveType || 'unknown' },
        lastHealthCheck: { checkedAt: new Date().toISOString() },
      },
      metadata: { source: 'backend', sourceErrors: [] },
    };
  } catch (err) {
    console.warn('[DiagnosticsStore] Backend unreachable, building client-side bundle', err.message);
    return _fallbackBundle(scope, user);
  }
}

export async function getQueueDiagnostics(scope = {}) {
  try {
    const { data } = await axios.get(`${API}/api/diagnostics/overview`, {
      headers: authHeaders(),
    });
    return {
      available: true,
      snapshot: {
        pendingCount: data.orders?.pending || 0,
        failedCount: data.orders?.failed_24h || 0,
        conflictCount: 0,
      },
      itemCount: data.orders?.total_24h || 0,
    };
  } catch {
    return { available: false, error: 'Backend unreachable' };
  }
}

export async function getIncidentDiagnostics(scope = {}) {
  try {
    const { data } = await axios.get(`${API}/api/diagnostics/overview`, {
      headers: authHeaders(),
    });
    return {
      available: true,
      open: data.alerts?.open || 0,
      critical: data.alerts?.critical || 0,
    };
  } catch {
    return { available: false, open: 0, critical: 0 };
  }
}

export function getNetworkDiagnostics() {
  return {
    available: true,
    online: navigator.onLine,
    heartbeatOk: null,
    lastCheckedAt: Date.now(),
    effectiveType: navigator.connection?.effectiveType || null,
    downlink: navigator.connection?.downlink || null,
  };
}

export function getAppContextDiagnostics() {
  return {
    available: true,
    appVersion: process.env.REACT_APP_VERSION || '1.0.0',
    environment: process.env.NODE_ENV || 'production',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    browser: detectBrowser(),
    pwaInstalled: window.matchMedia?.('(display-mode: standalone)').matches || false,
  };
}

// ─── Exports ───

export async function exportDiagnosticsBundleJson(scope) {
  const bundle = await getDiagnosticsBundle(scope);
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `diagnostics-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportDiagnosticsSummaryCsv(scope) {
  const bundle = await getDiagnosticsBundle(scope);
  const rows = [
    ['Section', 'Metric', 'Value'],
    ['Orders', 'Pending', bundle.queueSnapshot?.pendingCount || 0],
    ['Orders', 'Failed', bundle.queueSnapshot?.failedCount || 0],
    ['Alerts', 'Open', bundle.incidentSummary?.openCount || 0],
    ['Alerts', 'Critical', bundle.incidentSummary?.criticalCount || 0],
    ['Network', 'Status', bundle.networkDiagnostics?.currentStatus || 'unknown'],
    ['App', 'Version', bundle.appContext?.appVersion || 'N/A'],
  ];
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `diagnostics-summary-${Date.now()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── Helpers ───

function detectBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

async function _fallbackBundle(scope, user) {
  return sanitizeDiagnosticsBundle(
    buildDiagnosticsBundle({
      scope,
      user,
      queueItems: [],
      syncLogs: [],
      incidents: [],
      networkStatus: { online: navigator.onLine },
      featureFlags: {},
      policies: {},
    })
  );
}

export default {
  getDiagnosticsBundle,
  getQueueDiagnostics,
  getIncidentDiagnostics,
  getNetworkDiagnostics,
  getAppContextDiagnostics,
};
